import {
  TX_TRANSITION_ACTOR_CUSTOMER,
  TX_TRANSITION_ACTOR_PROVIDER,
  getProcess,
  resolveLatestProcessName,
} from '../transactions/transaction';
import { isTalkSessionTransaction } from './sessionPricing';
import { isTalkerLateCancelCharge } from './cancelPolicy';

/** Deep-link hash that opens the review form on the order/sale page. */
export const LEAVE_REVIEW_HASH = 'leave-review';
export const HOW_IT_WENT_HASH = 'how-it-went';

const CUSTOMER_REVIEW_STATES = ['delivered', 'reviewed-by-provider'];
const PROVIDER_REVIEW_STATES = ['delivered', 'reviewed-by-customer'];
const TALKER_REVIEW_TRANSITIONS = new Set([
  'transition/review-1-by-customer',
  'transition/review-2-by-customer',
]);

/**
 * Current process state for a transaction, or null when the process is unknown.
 *
 * @param {Object} tx
 * @returns {string|null}
 */
export const getTransactionReviewState = tx => {
  try {
    const processName = resolveLatestProcessName(tx?.attributes?.processName);
    if (!processName) {
      return null;
    }
    const process = getProcess(processName);
    return process.getState(tx) || null;
  } catch (e) {
    return null;
  }
};

/**
 * True when the talker already left a public review. Expire-from-delivered
 * can land on `reviewed` without anyone reviewing — that stays false.
 *
 * @param {Object} [tx]
 * @returns {boolean}
 */
const transitionName = value =>
  typeof value === 'string' ? value : value?.transition || value?.attributes?.name || '';

const talkerReviewedInHistory = tx => {
  const transitions = tx?.attributes?.transitions;
  if (!Array.isArray(transitions)) {
    return false;
  }
  return transitions.some(item => TALKER_REVIEW_TRANSITIONS.has(transitionName(item)));
};

export const talkerHasPublicReview = tx => {
  const last = tx?.attributes?.lastTransition;
  if (TALKER_REVIEW_TRANSITIONS.has(last) || talkerReviewedInHistory(tx)) {
    return true;
  }
  // After the talker reviews, the listener window can expire into `reviewed`.
  if (last === 'transition/expire-provider-review-period') {
    return true;
  }
  return getTransactionReviewState(tx) === 'reviewed-by-customer';
};

/**
 * True when this party can still leave a review after a completed session.
 * Strangers and unfinished bookings stay false.
 *
 * @param {Object} tx
 * @param {string} transactionRole customer | provider
 * @returns {boolean}
 */
export const canLeaveReview = (tx, transactionRole) => {
  if (!tx?.id?.uuid || !transactionRole) {
    return false;
  }
  // A late cancel (charged per policy) was never a session: nothing to review.
  if (isTalkerLateCancelCharge(tx)) {
    return false;
  }
  const state = getTransactionReviewState(tx);
  if (!state) {
    return false;
  }
  if (transactionRole === TX_TRANSITION_ACTOR_CUSTOMER || transactionRole === 'customer') {
    return CUSTOMER_REVIEW_STATES.includes(state);
  }
  if (transactionRole === TX_TRANSITION_ACTOR_PROVIDER || transactionRole === 'provider') {
    // Listeners leave a private thumbs note, not a public Sharetribe review.
    if (isTalkSessionTransaction(tx)) {
      return false;
    }
    return PROVIDER_REVIEW_STATES.includes(state);
  }
  return false;
};

/**
 * NamedLink props that open the review form on the matching order/sale page.
 *
 * @param {Object} tx
 * @param {string} transactionRole
 * @returns {{ name: string, params: { id: string }, to: { hash: string } }|null}
 */
export const leaveReviewLinkProps = (tx, transactionRole) => {
  if (canLeavePrivateFeedback(tx, transactionRole)) {
    return {
      name: 'SaleDetailsPage',
      params: { id: tx.id.uuid },
      to: { hash: `#${HOW_IT_WENT_HASH}` },
    };
  }
  if (!canLeaveReview(tx, transactionRole)) {
    return null;
  }
  const isCustomer =
    transactionRole === TX_TRANSITION_ACTOR_CUSTOMER || transactionRole === 'customer';
  return {
    name: isCustomer ? 'OrderDetailsPage' : 'SaleDetailsPage',
    params: { id: tx.id.uuid },
    to: { hash: `#${LEAVE_REVIEW_HASH}` },
  };
};

/**
 * Completed sessions this party still needs to review.
 *
 * @param {Array<Object>} transactions
 * @param {string} transactionRole
 * @returns {Array<Object>}
 */
export const pendingLeaveReviews = (transactions, transactionRole) =>
  (Array.isArray(transactions) ? transactions : []).filter(tx =>
    canLeaveReview(tx, transactionRole)
  );

/**
 * Completed talk sessions the listener still needs a private thumbs note for.
 *
 * @param {Array<Object>} transactions
 * @param {string} [transactionRole]
 * @returns {Array<Object>}
 */
export const pendingPrivateFeedback = (
  transactions,
  transactionRole = TX_TRANSITION_ACTOR_PROVIDER
) =>
  (Array.isArray(transactions) ? transactions : []).filter(tx =>
    needsPrivateFeedback(tx, transactionRole)
  );

/**
 * Welcome/Inbox target for pending reviews: one order page, or the Inbox list.
 *
 * @param {Array<Object>} transactions
 * @param {string} transactionRole
 * @returns {{ name: string, params: Object, to?: { hash: string } }|null}
 */
export const welcomeLeaveReviewTarget = (transactions, transactionRole) => {
  const pending = pendingLeaveReviews(transactions, transactionRole);
  if (pending.length === 1) {
    return leaveReviewLinkProps(pending[0], transactionRole);
  }
  if (pending.length > 1) {
    const isCustomer =
      transactionRole === TX_TRANSITION_ACTOR_CUSTOMER || transactionRole === 'customer';
    return { name: 'InboxPage', params: { tab: isCustomer ? 'orders' : 'sales' } };
  }
  return null;
};

/**
 * Welcome/Inbox target for pending listener thumbs notes.
 *
 * @param {Array<Object>} transactions
 * @returns {{ name: string, params: Object, to?: { hash: string } }|null}
 */
export const welcomeHowItWentTarget = transactions => {
  const pending = pendingPrivateFeedback(transactions, TX_TRANSITION_ACTOR_PROVIDER);
  if (pending.length === 1) {
    return leaveReviewLinkProps(pending[0], TX_TRANSITION_ACTOR_PROVIDER);
  }
  if (pending.length > 1) {
    return { name: 'InboxPage', params: { tab: 'sales' } };
  }
  return null;
};

/**
 * True when the listener already saved thumbs / a private note.
 * Reads Integration `metadata.listenerFeedback` (and a protectedData
 * copy if the inbox query attached one).
 *
 * @param {Object} [tx]
 * @returns {boolean}
 */
export const hasListenerFeedback = tx => {
  const attrs = tx?.attributes || {};
  const raw = attrs.metadata?.listenerFeedback || attrs.protectedData?.listenerFeedback;
  if (raw && typeof raw === 'object') {
    return !!(raw.thumb || raw.rating || raw.at || raw.updatedAt || raw.note);
  }
  return attrs.protectedData?.hasListenerFeedback === true;
};

/**
 * Listener private "How did it go?" after a completed talk session.
 * Still true after a save so the sale page can update the note.
 *
 * @param {Object} tx
 * @param {string} transactionRole
 * @returns {boolean}
 */
export const canLeavePrivateFeedback = (tx, transactionRole) => {
  if (!tx?.id?.uuid) {
    return false;
  }
  if (isTalkerLateCancelCharge(tx)) {
    return false;
  }
  if (transactionRole !== TX_TRANSITION_ACTOR_PROVIDER && transactionRole !== 'provider') {
    return false;
  }
  if (!isTalkSessionTransaction(tx)) {
    return false;
  }
  const state = getTransactionReviewState(tx);
  return PROVIDER_REVIEW_STATES.includes(state);
};

/**
 * Inbox / dashboard prompt: completed talk session, listener, no save yet.
 *
 * @param {Object} tx
 * @param {string} transactionRole
 * @returns {boolean}
 */
export const needsPrivateFeedback = (tx, transactionRole) =>
  canLeavePrivateFeedback(tx, transactionRole) && !hasListenerFeedback(tx);

export const isLeaveReviewHash = hash => {
  if (!hash || typeof hash !== 'string') {
    return false;
  }
  return hash === `#${LEAVE_REVIEW_HASH}` || hash === LEAVE_REVIEW_HASH;
};

/**
 * True when the URL hash should scroll to the private thumbs prompt.
 *
 * @param {string} [hash]
 * @returns {boolean}
 */
export const isHowItWentHash = hash => {
  if (!hash || typeof hash !== 'string') {
    return false;
  }
  return hash === `#${HOW_IT_WENT_HASH}` || hash === HOW_IT_WENT_HASH;
};

const uuidOf = value => {
  if (!value) {
    return null;
  }
  if (typeof value === 'string') {
    return value;
  }
  return value.uuid || value.id?.uuid || null;
};

/**
 * Pick the most recently completed, still-unreviewed order for this listener.
 * Used on the public Listening profile. Only customer-side reviews.
 *
 * @param {Array<Object>} transactions
 * @param {Object} [filters]
 * @param {string} [filters.listingId]
 * @param {string} [filters.providerId]
 * @param {string} [filters.currentUserId]
 * @returns {Object|null}
 */
export const pickLeaveReviewTransaction = (transactions, filters = {}) => {
  const { listingId, providerId, currentUserId } = filters;
  const list = Array.isArray(transactions) ? transactions : [];
  const matches = list.filter(tx => {
    const listingUuid = uuidOf(tx?.listing?.id);
    const providerUuid = uuidOf(tx?.provider?.id);
    const customerUuid = uuidOf(tx?.customer?.id);
    if (listingId && listingUuid && listingUuid !== listingId) {
      return false;
    }
    if (providerId && providerUuid && providerUuid !== providerId) {
      return false;
    }
    if (currentUserId && customerUuid && customerUuid !== currentUserId) {
      return false;
    }
    return canLeaveReview(tx, TX_TRANSITION_ACTOR_CUSTOMER);
  });
  if (matches.length <= 1) {
    return matches[0] || null;
  }
  return [...matches].sort((a, b) => {
    const aAt = new Date(a?.attributes?.lastTransitionedAt || 0).getTime();
    const bAt = new Date(b?.attributes?.lastTransitionedAt || 0).getTime();
    return bAt - aAt;
  })[0];
};
